import React, { useEffect, useMemo, useState } from "react";
import Tag from "../Tag";
import Select from "../Select";
import axiosBackend from "../../api/axiosBackend";
import CopyText from '../CopyText';

// shadcn
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "components/ui/card";
import { Button } from "components/ui/button";
import { Separator } from "components/ui/separator";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";

// icons
import { Pencil, X, Save } from "lucide-react";
import OrderActions from "../OrdersComponents/OrderActions";


const statusOptions = [
  { value: "all", label: "כל הסטטוסים", color: "transparent" },
  { value: "pending", label: "ממתין", color: "#A8D5BA" },
  { value: "inProgress", label: "בתהליך", color: "#FFD59E" },
  { value: "done", label: "הסתיים", color: "#F7A8A8" },
];

const findKeyByValue = (arr, value) => {
  const entry = arr.find((item) => item.label === value);
  return entry ? entry.value : undefined;
};

const buildPatch = (original, draft) => {
  // keep this minimal; only send fields you allow editing inline
  const fields = ["name", "phone", "product", "comments"];
  const patch = {};
  for (const f of fields) {
    const a = (original?.[f] ?? "").toString();
    const b = (draft?.[f] ?? "").toString();
    if (a !== b) patch[f] = b;
  }
  return patch;
};

const validateDraft = (draft) => {
  if (!draft?.name?.trim()) return "שם חובה";
  if (!draft?.phone?.trim()) return "מספר פלאפון חובה";
  return "";
};

const OrderItem = ({ order, onStatusChange, onSaved }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({
    name: order?.name || "",
    phone: order?.phone || "",
    product: order?.product || "",
    comments: order?.comments || "",
  }));
  const [saving, setSaving] = useState(false);

  // if order updates from parent (e.g., after a save), sync when not editing
  useEffect(() => {
    if (!isEditing) {
      setDraft({
        name: order?.name || "",
        phone: order?.phone || "",
        product: order?.product || "",
        comments: order?.comments || "",
      });
    }
  }, [order, isEditing]);

  const statusInitialValue = useMemo(
    () => findKeyByValue(statusOptions, order.status),
    [order.status]
  );

  const onChange = (e) => {
    const { name, value } = e.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setDraft({
      name: order?.name || "",
      phone: order?.phone || "",
      product: order?.product || "",
      comments: order?.comments || "",
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    const v = validateDraft(draft);
    if (v) {
      alert(v);
      return;
    }

    const patch = buildPatch(order, draft);
    if (Object.keys(patch).length === 0) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await axiosBackend.put(`/orders/modify/${order._id}`, { ...order, ...patch });
      // notify parent to update local list (single source of truth)
      onSaved?.(order._id, patch);
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      alert("Error updating order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="space-y-3">
          {/* Row 1: name + actions */}
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="min-w-0 flex-1 truncate text-lg">
              {order.name}
            </CardTitle>

            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => setIsEditing(true)}
                aria-label="עריכה"
                title="עריכה"
              >
                <Pencil className="h-4 w-4" />
              </Button>

              <OrderActions order={order} disabled={saving} />
            </div>
          </div>

          {/* Row 2: status (mobile full width, desktop stays compact on the right) */}
          <div className="flex justify-end">
            <div className="w-full sm:w-auto sm:min-w-[160px]">
              <Select
                options={statusOptions}
                initialValue={findKeyByValue(statusOptions, order.status)}
                onOptionChange={(newStatus) => onStatusChange(order._id, newStatus)}
              />
            </div>
          </div>
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="space-y-3 pt-4">
        {/* phone */}
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">מספר פלאפון:</span>{" "}
          {isEditing ? (
            <Input
              name="phone"
              value={draft.phone}
              onChange={onChange}
              className="mt-2 h-9"
              placeholder="מספר פלאפון"
              disabled={saving}
            />
          ) : (
            <CopyText value={order.phone}>
              <span className="font-medium text-foreground">{order.phone}</span>
            </CopyText>
          )}
        </div>

        {/* product */}
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">מוצר:</span>{" "}
          {isEditing ? (
            <Input
              name="product"
              value={draft.product}
              onChange={onChange}
              className="mt-2 h-9"
              placeholder="מוצר"
              disabled={saving}
            />
          ) : (
            order.product
          )}
        </div>

        {/* comments */}
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">הערות:</span>{" "}
          {isEditing ? (
            <Textarea
              name="comments"
              value={draft.comments}
              onChange={onChange}
              className="mt-2"
              placeholder="הערות"
              disabled={saving}
            />
          ) : (
            order.comments
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">תאריך:</span>{" "}
          {order.date
            ? new Date(order.date).toLocaleDateString("he-IL", {
              timeZone: "Asia/Jerusalem",
            })
            : ""}
        </p>

        {order.tags?.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-2">
            {order.tags.map((tag) => (
              <Tag key={tag._id} name={tag.name} />
            ))}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex items-center justify-between gap-2">
        {isEditing && (
          <>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={handleCancel}
              disabled={saving}
            >
              <X className="ml-2 h-4 w-4" />
              ביטול
            </Button>

            <Button
              type="button"
              className="w-full"
              onClick={handleSave}
              disabled={saving}
            >
              <Save className="ml-2 h-4 w-4" />
              {saving ? "שומר…" : "שמור"}
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};

export default OrderItem;
